import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Ban } from "lucide-react";
import Loader from "react-spinner-loader";
import { getData } from "../../../../../Services/Services";
import { getSopMasterData, getSopInstanceResourceById, getSopInstanceSummaryBySopInstance } from "../../../../../Services/EndPoints";

const STATUS_LABEL_MAP = {
  Started: "Started",
  "In-Progress": "In Progress",
  Completed: "Completed",
  Cancel: "Canceled",
  Closed: "Closed",
};

const STATUS_BADGE_PALETTE = {
  Started: { background: "#DBEAFE", color: "#1D4ED8", border: "#BFDBFE", dot: "#3B82F6" },
  "In-Progress": { background: "#FEF3C7", color: "#92400E", border: "#FDE68A", dot: "#F59E0B" },
  Completed: { background: "#DCFCE7", color: "#15803D", border: "#BBF7D0", dot: "#22C55E" },
  Cancel: { background: "#FEE2E2", color: "#B91C1C", border: "#FECACA", dot: "#EF4444" },
  Closed: { background: "#F1F5F9", color: "#64748B", border: "#E2E8F0", dot: "#94A3B8" },
};

const badgeBaseStyle = {
  display: "inline-block",
  padding: "3px 12px",
  borderRadius: "12px",
  fontSize: "12px",
  fontWeight: 600,
  whiteSpace: "nowrap",
};

const getStatusBadgeStyle = (status) => {
  const palette = STATUS_BADGE_PALETTE[status] || { background: "#F1F5F9", color: "#64748B", border: "#E2E8F0", dot: "#94A3B8" };
  return { ...badgeBaseStyle, background: palette.background, color: palette.color, border: `1px solid ${palette.border}`, dot: palette.dot };
};

const getSeverityBadgeStyle = (severity) => {
  const s = (severity || "").toLowerCase();
  let palette = { background: "#eef2f7", color: "#5b6b7a", border: "#d5e0ea" };
  if (s.includes("minor") || s.includes("level 1")) {
    palette = { background: "#e6f4ea", color: "#1e7e34", border: "#b7e1c1" };
  } else if (s.includes("moderate") || s.includes("level 2")) {
    palette = { background: "#fff4e0", color: "#b26a00", border: "#f2d9a8" };
  } else if (s.includes("major") || s.includes("level 3")) {
    palette = { background: "#ffe9d9", color: "#c2410c", border: "#f3c2a0" };
  } else if (s.includes("critical") || s.includes("level 4")) {
    palette = { background: "#fde5e5", color: "#c0392b", border: "#f2b8b8" };
  }
  return { ...badgeBaseStyle, background: palette.background, color: palette.color, border: `1px solid ${palette.border}` };
};

const getTaskProgressPercent = (taskSummary) => taskSummary?.progressPercent ?? 0;

// Humanizes a raw elapsed-minutes value into the largest sensible unit —
// e.g. 1.5 -> "2 min", 90 -> "2 hr", 1500 -> "1 day", 44000 -> "1 month".
const formatElapsedDuration = (minutesValue) => {
  if (minutesValue === undefined || minutesValue === null || isNaN(Number(minutesValue))) return "NA";
  const pluralize = (n, unit) => `${n} ${unit}${n === 1 ? "" : "s"}`;
  const totalMinutes = Number(minutesValue);
  const totalSeconds = totalMinutes * 60;

  // Round each candidate BEFORE checking the unit boundary — otherwise a raw
  // value like 59.6 min passes the "< 60 min" check but then displays as the
  // rounded "60 mins" instead of correctly bumping up to "1 hr".
  const roundedSeconds = Math.round(totalSeconds);
  if (roundedSeconds < 60) return pluralize(roundedSeconds, "sec");

  const roundedMinutes = Math.round(totalMinutes);
  if (roundedMinutes < 60) return pluralize(roundedMinutes, "min");

  const totalHours = totalMinutes / 60;
  const roundedHours = Math.round(totalHours);
  if (roundedHours < 24) return pluralize(roundedHours, "hr");

  const totalDays = totalHours / 24;
  const roundedDays = Math.round(totalDays);
  if (roundedDays < 30) return pluralize(roundedDays, "day");

  if (roundedDays < 365) {
    const totalMonths = totalDays / 30;
    return pluralize(Math.round(totalMonths), "month");
  }

  const totalYears = totalDays / 365;
  return pluralize(Math.round(totalYears), "year");
};

const formatDateTime = (val) => {
  if (!val) return "NA";
  const d = new Date(val);
  if (isNaN(d.getTime())) return String(val);
  const pad = (n) => String(n).padStart(2, "0");
  const hours12 = d.getHours() % 12 || 12;
  const ampm = d.getHours() < 12 ? "AM" : "PM";
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}, ${pad(hours12)}:${pad(d.getMinutes())} ${ampm}`;
};

const fieldWrap = { display: "flex", flexDirection: "column", gap: "4px" };
const fieldLabel = {
  fontSize: "10px",
  fontWeight: 800,
  letterSpacing: ".4px",
  textTransform: "uppercase",
  color: "#98A2B3",
};
const fieldValue = { fontSize: "13.5px", fontWeight: 600, color: "#1D2939" };

const resourceThStyle = {
  padding: "8px 10px",
  textAlign: "left",
  fontWeight: 600,
  color: "#fff",
  fontSize: "11px",
  border: "none",
  background: "#1C4E72",
};
const resourceTdStyle = {
  padding: "7px 10px",
  fontSize: "12px",
  color: "#1D2939",
  borderBottom: "1px solid #eef1f4",
  textAlign: "left",
};

const TASK_STATUS_MAP = {
  TASK_EXE_STATUS_NEW_TASK: { color: "#B2EAFE", label: "Started" },
  TASK_EXE_STATUS_INPROGRESS: { color: "#fccd7c", label: "In Progress" },
  TASK_EXE_STATUS_COMPLETED: { color: "#7cdb90", label: "Completed" },
  TASK_EXE_STATUS_CANCEL: { color: "#e95060", label: "Canceled" },
  TASK_EXE_STATUS_CLOSED: { color: "Gray", label: "Closed" },
};
const TASK_PRIORITY_MAP = {
  0: { color: "#b2eafe", label: "Low" },
  1: { color: "#fccd7c", label: "Medium" },
  2: { color: "#e87c86", label: "High" },
};

const ViewSopInstance = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const data = state?.data;

  const [loader, setLoader] = useState(false);
  const [resources, setResources] = useState([]);
  const [cancelReason, setCancelReason] = useState(null);
  const [cancelRemarks, setCancelRemarks] = useState(null);
  const [severityHistory, setSeverityHistory] = useState([]);
  const [taskList, setTaskList] = useState([]);
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [incidentSeqId, setIncidentSeqId] = useState(null);
  const [taskSummary, setTaskSummary] = useState(null);
  const [slaInfo, setSlaInfo] = useState({
    sla_hours: data?.sla_hours,
    target_datetime: data?.target_datetime,
    sla_elapsed_minutes: data?.sla_elapsed_minutes,
    sla_breached: data?.sla_breached,
  });
  // Only present once the instance is actually cancelled/completed — render
  // conditionally wherever these are shown, never as a bare "NA" field.
  const [lifecycleInfo, setLifecycleInfo] = useState({
    cancelled_on: data?.cancelled_on,
    completed_on: data?.completed_on,
  });

  useEffect(() => {
    if (!data) return;
    const id = data.id;
    if (!id) return;

    const fetchExtras = async () => {
      setLoader(true);
      try {
        const [masterList, resourceList] = await Promise.all([
          getData(getSopMasterData + id),
          getData(getSopInstanceResourceById + id),
        ]);
        const list = Array.isArray(masterList) ? masterList : [];
        const master = list[0] || null;
        setCancelReason(master?.cancelReason || null);
        setCancelRemarks(master?.cancelRemarks || null);
        setSeverityHistory(master?.severityHistory || []);
        setResources(Array.isArray(resourceList) ? resourceList : []);
        setTaskList(
          list.map((task) => ({
            id: task.id,
            taskName: task.taskName,
            taskType: task.taskType,
            createdOn: task.createdOn,
            taskDescription: task.taskDescription,
            userTaskMapList: (task.sopTaskInstanceEntityList || []).map((t) => ({
              ...t,
              parent_task_type: task.taskType || "341",
            })),
          }))
        );

        setSlaInfo((prev) => ({
          sla_hours: master?.sla_hours ?? prev.sla_hours,
          target_datetime: master?.target_datetime ?? prev.target_datetime,
          sla_elapsed_minutes: master?.sla_elapsed_minutes ?? prev.sla_elapsed_minutes,
          sla_breached: master?.sla_breached ?? prev.sla_breached,
        }));
        setLifecycleInfo((prev) => ({
          cancelled_on: master?.cancelled_on ?? prev.cancelled_on,
          completed_on: master?.completed_on ?? prev.completed_on,
        }));

        const seqId = master?.incidentSeqId || null;
        setIncidentSeqId(seqId);
        if (seqId) {
          const summary = await getData(
            getSopInstanceSummaryBySopInstance.replace("SOPINSTANCEID", id).replace("INCIDENTID", seqId)
          );
          setTaskSummary(summary?.taskSummary || null);
        }
      } catch (error) {
        console.error("Error fetching SOP instance details:", error);
      } finally {
        setLoader(false);
      }
    };
    fetchExtras();
  }, [data]);

  if (!data) {
    return (
      <div style={{ margin: "8px" }}>
        <div style={{ padding: "20px", textAlign: "center", color: "#98A2B3" }}>
          No SOP instance data found.
        </div>
      </div>
    );
  }

  const label = data.status ? STATUS_LABEL_MAP[data.status] || data.status : "NA";
  const { dot, ...badgeStyle } = getStatusBadgeStyle(data.status);
  const createdByFullName =
    data.created_by_firstname && data.created_by_lastname
      ? `${data.created_by_firstname} ${data.created_by_lastname}`
      : data.created_by_firstname || "";
  const createdByName = createdByFullName
    ? data.created_by_role_name
      ? `${createdByFullName} (${data.created_by_role_name})`
      : createdByFullName
    : "NA";

  const automaticResources = resources.filter((r) => r.is_automatic === true);
  const manualResources = resources.filter((r) => r.is_manual === true);

  const renderResourceTable = (title, rows, headerColor) =>
    rows.length > 0 && (
      <div style={{ flex: "1", minWidth: "280px" }}>
        <div style={{ fontSize: "11px", fontWeight: 700, color: headerColor, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "8px" }}>
          {title}
        </div>
        <div style={{ background: "#fff", borderRadius: "8px", border: "1px solid #EEF1F5", overflow: "hidden", maxHeight: "260px", overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ position: "sticky", top: 0 }}>
                <th style={{ ...resourceThStyle, background: headerColor }}>Resource Name</th>
                <th style={{ ...resourceThStyle, background: headerColor }}>Facility</th>
                <th style={{ ...resourceThStyle, background: headerColor, textAlign: "center" }}>Available</th>
                <th style={{ ...resourceThStyle, background: headerColor, textAlign: "center" }}>Qty</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={idx} style={{ background: idx % 2 === 0 ? "#fff" : "#F9FAFB" }}>
                  <td style={resourceTdStyle}>{r.name || "NA"}</td>
                  <td style={resourceTdStyle}>{r.facility_name ?? "-"}</td>
                  <td style={{ ...resourceTdStyle, textAlign: "center", fontWeight: 700 }}>{r.available_quantity ?? "-"}</td>
                  <td style={{ ...resourceTdStyle, textAlign: "center", fontWeight: 700 }}>{r.required_quantity ?? "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );

  return (
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@500;600;700;800&display=swap');`}</style>
      <div style={{ margin: "8px", fontFamily: "'Poppins', 'Segoe UI', sans-serif" }}>
        <Loader show={loader} type="body" stack="vertical" message="Loading Data" />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "10px 14px",
            borderRadius: "10px",
            background: "linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%)",
            boxShadow: "0 2px 6px rgba(46,109,164,0.2)",
          }}
        >
          <ArrowLeft size={18} color="#fff" className="cursor-pointer" onClick={() => navigate(-1)} />
          <div style={{ fontSize: "14px", fontWeight: 800, color: "#fff", letterSpacing: ".2px" }}>
            View SOP Instance
          </div>
        </div>

        <div style={{ marginTop: "10px", backgroundColor: "#fff", borderRadius: "10px", padding: "20px", boxShadow: "0 1px 2px rgba(16,24,40,0.04)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "18px", marginBottom: "18px" }}>
            <div style={fieldWrap}>
              <span style={fieldLabel}>SOP Instance ID</span>
              <span style={fieldValue}>{data.sopinstance_seq_id || "NA"}</span>
            </div>
            <div style={fieldWrap}>
              <span style={fieldLabel}>Instance Name</span>
              <span style={fieldValue} title={data.name}>{data.name || "NA"}</span>
            </div>
            <div style={fieldWrap}>
              <span style={fieldLabel}>Status</span>
              <span style={{ ...badgeStyle, alignSelf: "flex-start" }}>
                <span style={{ display: "inline-block", width: "6px", height: "6px", marginRight: "6px", backgroundColor: dot, borderRadius: "50%" }}></span>
                {label}
              </span>
            </div>
            <div style={fieldWrap}>
              <span style={fieldLabel}>Trigger Type</span>
              <span style={fieldValue}>{data.is_auto_triggered ? "Auto" : "Manual"}</span>
            </div>

            <div style={fieldWrap}>
              <span style={fieldLabel}>Incident ID</span>
              <span style={fieldValue}>{incidentSeqId || "NA"}</span>
            </div>
            <div style={fieldWrap}>
              <span style={fieldLabel}>Incident Name</span>
              <span style={fieldValue}>{data.incident || "NA"}</span>
            </div>
            <div style={fieldWrap}>
              <span style={fieldLabel}>Incident Category</span>
              <span style={fieldValue}>{data.hazard_type || data.category || "NA"}</span>
            </div>
            <div style={fieldWrap}>
              <span style={fieldLabel}>Severity</span>
              {data.incident_severity ? (
                <span style={{ ...getSeverityBadgeStyle(data.incident_severity), alignSelf: "flex-start" }}>{data.incident_severity}</span>
              ) : (
                <span style={fieldValue}>NA</span>
              )}
            </div>

            <div style={fieldWrap}>
              <span style={fieldLabel}>Region</span>
              <span style={fieldValue}>{data.region_names || "NA"}</span>
            </div>
            <div style={fieldWrap}>
              <span style={fieldLabel}>SOP Name</span>
              <span style={fieldValue}>{data.sop || "NA"}</span>
            </div>
            <div style={fieldWrap}>
              <span style={fieldLabel}>Created By</span>
              <span style={fieldValue}>{createdByName}</span>
            </div>
            <div style={fieldWrap}>
              <span style={fieldLabel}>Created On</span>
              <span style={fieldValue}>{formatDateTime(data.created_on)}</span>
            </div>

            <div style={fieldWrap}>
              <span style={fieldLabel}>SLA Hours</span>
              <span style={fieldValue}>{slaInfo.sla_hours ?? "NA"}</span>
            </div>
            <div style={fieldWrap}>
              <span style={fieldLabel}>Target Time</span>
              <span style={fieldValue}>{formatDateTime(slaInfo.target_datetime)}</span>
            </div>
            <div style={fieldWrap}>
              <span style={fieldLabel}>SLA Elapsed</span>
              <span
                style={fieldValue}
                title={
                  slaInfo.sla_elapsed_minutes !== undefined && slaInfo.sla_elapsed_minutes !== null
                    ? `${Number(slaInfo.sla_elapsed_minutes).toFixed(5)} min`
                    : undefined
                }
              >
                {formatElapsedDuration(slaInfo.sla_elapsed_minutes)}
              </span>
            </div>
            <div style={fieldWrap}>
              <span style={fieldLabel}>SLA Breached</span>
              <span
                style={{
                  ...badgeBaseStyle,
                  alignSelf: "flex-start",
                  background: slaInfo.sla_breached ? "#FEE2E2" : "#DCFCE7",
                  color: slaInfo.sla_breached ? "#B91C1C" : "#15803D",
                  border: `1px solid ${slaInfo.sla_breached ? "#FECACA" : "#BBF7D0"}`,
                }}
              >
                {slaInfo.sla_breached ? "Yes" : "No"}
              </span>
            </div>
            {lifecycleInfo.cancelled_on && (
              <div style={fieldWrap}>
                <span style={fieldLabel}>Cancelled On</span>
                <span style={fieldValue}>{formatDateTime(lifecycleInfo.cancelled_on)}</span>
              </div>
            )}
            {lifecycleInfo.completed_on && (
              <div style={fieldWrap}>
                <span style={fieldLabel}>Completed On</span>
                <span style={fieldValue}>{formatDateTime(lifecycleInfo.completed_on)}</span>
              </div>
            )}
          </div>

          <div style={fieldWrap}>
            <span style={fieldLabel}>SOP Description</span>
            <span style={{ ...fieldValue, fontWeight: 400, marginTop: "4px" }}>{data.description || "NA"}</span>
          </div>

          {data.status === "Cancel" && (cancelReason || cancelRemarks) && (
            <div style={{ marginTop: "20px", background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "10px", padding: "16px 20px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
                <Ban size={16} color="#dc2626" />
                <span style={{ fontWeight: 700, fontSize: "14px", color: "#dc2626" }}>Cancellation Details</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 3fr", gap: "16px" }}>
                <div>
                  <div style={fieldLabel}>Cancel Reason</div>
                  <div style={{ fontSize: "14px", fontWeight: 700, color: "#dc2626", marginTop: "4px" }}>{cancelReason || "NA"}</div>
                </div>
                <div>
                  <div style={fieldLabel}>Remarks</div>
                  <div style={{ fontSize: "14px", color: "#374151", marginTop: "4px" }}>{cancelRemarks || "NA"}</div>
                </div>
              </div>
            </div>
          )}

          {severityHistory.length > 0 && (
            <div style={{ marginTop: "20px" }}>
              <div style={{ fontWeight: 700, fontSize: "13px", color: "#1C4E72", marginBottom: "10px" }}>
                Severity Change History
              </div>
              <div style={{ border: "1px solid #EEF1F5", borderRadius: "8px", overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={resourceThStyle}>S.No</th>
                      <th style={{ ...resourceThStyle, background: "#1C4E72" }}>Old Severity</th>
                      <th style={{ ...resourceThStyle, background: "#1C4E72" }}>New Severity</th>
                      <th style={{ ...resourceThStyle, background: "#1C4E72" }}>Changed By</th>
                      <th style={{ ...resourceThStyle, background: "#1C4E72" }}>Remarks</th>
                      <th style={{ ...resourceThStyle, background: "#1C4E72" }}>Changed On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {severityHistory.map((item, idx) => (
                      <tr key={item.id || idx} style={{ background: idx % 2 === 0 ? "#fff" : "#F9FAFB" }}>
                        <td style={resourceTdStyle}>{idx + 1}</td>
                        <td style={resourceTdStyle}>{item.oldSeverity || "-"}</td>
                        <td style={resourceTdStyle}>{item.newSeverity || "-"}</td>
                        <td style={resourceTdStyle}>{item.changedBy || "-"}</td>
                        <td style={resourceTdStyle}>{item.remarks || "-"}</td>
                        <td style={resourceTdStyle}>{item.changedOn || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div style={{ marginTop: "20px" }}>
            <div style={{ fontWeight: 700, fontSize: "13px", color: "#1C4E72", marginBottom: "10px" }}>
              Resource Allocation & Deployment Details
            </div>
            {resources.length > 0 ? (
              <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
                {renderResourceTable("Automatic", automaticResources, "#27ae60")}
                {renderResourceTable("Manual", manualResources, "#2980b9")}
              </div>
            ) : (
              <div style={{ color: "#98A2B3", fontSize: "13px", fontStyle: "italic" }}>No resources assigned</div>
            )}
          </div>

          {taskSummary && (
            <div style={{ marginTop: "20px" }}>
              <div style={{ fontWeight: 700, fontSize: "13px", color: "#1C4E72", marginBottom: "10px" }}>
                Task Summary
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "12px", marginBottom: "14px" }}>
                {[
                  { label: "Total Tasks", value: taskSummary.totalTasks ?? 0, color: "#1C4E72", bg: "#EFF6FF" },
                  { label: "Completed", value: taskSummary.completedTasks ?? 0, color: "#15803D", bg: "#DCFCE7" },
                  { label: "In Progress", value: taskSummary.inProgressTasks ?? 0, color: "#92400E", bg: "#FEF3C7" },
                  { label: "Started", value: taskSummary.notStartedTasks ?? 0, color: "#64748B", bg: "#F1F5F9" },
                  { label: "Cancelled", value: taskSummary.cancelledTasks ?? 0, color: "#B91C1C", bg: "#FEE2E2" },
                ].map((tile) => (
                  <div key={tile.label} style={{ background: tile.bg, borderRadius: "8px", padding: "10px 14px" }}>
                    <div style={{ fontSize: "18px", fontWeight: 800, color: tile.color }}>{tile.value}</div>
                    <div style={{ ...fieldLabel, marginTop: "2px" }}>{tile.label}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px" }}>
                <span style={fieldLabel}>Progress</span>
                <div style={{ flex: 1, height: "8px", background: "#F1F5F9", borderRadius: "4px", overflow: "hidden" }}>
                  <div style={{ width: `${getTaskProgressPercent(taskSummary)}%`, height: "100%", background: "#2E6DA4" }}></div>
                </div>
                <span style={{ fontSize: "12px", fontWeight: 700, color: "#1C4E72" }}>{getTaskProgressPercent(taskSummary)}%</span>
              </div>

              {(taskSummary.tasks || []).length > 0 && (
                <div style={{ border: "1px solid #EEF1F5", borderRadius: "8px", overflow: "hidden" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                    <colgroup>
                      <col style={{ width: "22%" }} />
                      <col style={{ width: "43%" }} />
                      <col style={{ width: "17.5%" }} />
                      <col style={{ width: "17.5%" }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th style={resourceThStyle}>Owner</th>
                        <th style={resourceThStyle}>Task Name</th>
                        <th style={{ ...resourceThStyle, textAlign: "center" }}>Priority</th>
                        <th style={{ ...resourceThStyle, textAlign: "center" }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {taskSummary.tasks.map((tsk, idx) => {
                        const priority = TASK_PRIORITY_MAP[tsk.priority] ?? TASK_PRIORITY_MAP[0];
                        const statusLabel = STATUS_LABEL_MAP[tsk.status] || tsk.status || "NA";
                        return (
                          <tr key={tsk.taskId || idx} style={{ background: idx % 2 === 0 ? "#fff" : "#F9FAFB" }}>
                            <td style={{ ...resourceTdStyle, wordBreak: "break-word" }}>{tsk.ownerName || "NA"}</td>
                            <td style={{ ...resourceTdStyle, wordBreak: "break-word" }}>{tsk.taskName || "NA"}</td>
                            <td style={{ ...resourceTdStyle, textAlign: "center" }}>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                <span style={{ width: "8px", height: "8px", backgroundColor: priority.color, borderRadius: "50%", flexShrink: 0 }}></span>
                                {priority.label}
                              </span>
                            </td>
                            <td style={{ ...resourceTdStyle, textAlign: "center" }}>{statusLabel}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: "20px" }}>
            <div style={{ fontWeight: 700, fontSize: "13px", color: "#1C4E72", marginBottom: "10px" }}>
              Task List
            </div>
            {taskList.length > 0 ? (
              <div style={{ border: "1px solid #EEF1F5", borderRadius: "8px", overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                  <colgroup>
                    <col style={{ width: "30px" }} />
                    <col style={{ width: "25%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "60%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={{ ...resourceThStyle, width: "30px" }}></th>
                      <th style={resourceThStyle}>Task Name</th>
                      <th style={resourceThStyle}>Created On</th>
                      <th style={resourceThStyle}>Task Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {taskList.map((task, idx) => {
                      const isExpanded = expandedTaskId === task.id;
                      const subTasks = task.userTaskMapList || [];
                      return (
                        <React.Fragment key={task.id || idx}>
                          <tr style={{ background: idx % 2 === 0 ? "#fff" : "#F9FAFB" }}>
                            <td style={{ ...resourceTdStyle, textAlign: "center" }}>
                              {subTasks.length > 0 && (
                                <span
                                  onClick={() => setExpandedTaskId(isExpanded ? null : task.id)}
                                  style={{ cursor: "pointer", fontSize: "16px", color: "#1C4E72" }}
                                >
                                  {isExpanded ? "-" : "+"}
                                </span>
                              )}
                            </td>
                            <td style={{ ...resourceTdStyle, wordBreak: "break-word" }}>{task.taskName || "NA"}</td>
                            <td style={{ ...resourceTdStyle, wordBreak: "break-word" }}>{formatDateTime(task.createdOn)}</td>
                            <td style={{ ...resourceTdStyle, wordBreak: "break-word" }}>{task.taskDescription?.trim() ? task.taskDescription : "NA"}</td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={4} style={{ padding: "10px 10px 14px 40px", background: idx % 2 === 0 ? "#fff" : "#F9FAFB" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #EEF1F5", tableLayout: "fixed" }}>
                                  <colgroup>
                                    <col style={{ width: "16%" }} />
                                    <col style={{ width: "22%" }} />
                                    <col style={{ width: "14%" }} />
                                    <col style={{ width: "26%" }} />
                                    <col style={{ width: "11%" }} />
                                    <col style={{ width: "11%" }} />
                                  </colgroup>
                                  <thead>
                                    <tr>
                                      <th style={{ ...resourceThStyle, background: "#4899b7" }}>Assign To</th>
                                      <th style={{ ...resourceThStyle, background: "#4899b7" }}>Task Name</th>
                                      <th style={{ ...resourceThStyle, background: "#4899b7" }}>Updated On</th>
                                      <th style={{ ...resourceThStyle, background: "#4899b7" }}>Action Message</th>
                                      <th style={{ ...resourceThStyle, background: "#4899b7" }}>Status</th>
                                      <th style={{ ...resourceThStyle, background: "#4899b7" }}>Priority</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {subTasks.map((subtask) => {
                                      const status = TASK_STATUS_MAP[subtask.status_code];
                                      const priority = TASK_PRIORITY_MAP[subtask.priority] || TASK_PRIORITY_MAP[0];
                                      return (
                                        <tr key={subtask.id}>
                                          <td style={{ ...resourceTdStyle, wordBreak: "break-word" }}>{subtask.user_name || "NA"}</td>
                                          <td style={{ ...resourceTdStyle, wordBreak: "break-word" }}>{subtask.task_name || "NA"}</td>
                                          <td style={{ ...resourceTdStyle, wordBreak: "break-word" }}>{formatDateTime(subtask.updated_on || subtask.created_on)}</td>
                                          <td style={{ ...resourceTdStyle, wordBreak: "break-word" }}>{subtask.description || "NA"}</td>
                                          <td style={resourceTdStyle}>
                                            {status ? (
                                              <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                <span style={{ width: "8px", height: "8px", backgroundColor: status.color, borderRadius: "50%", flexShrink: 0 }}></span>
                                                {status.label}
                                              </span>
                                            ) : (
                                              subtask.status_name || "NA"
                                            )}
                                          </td>
                                          <td style={resourceTdStyle}>
                                            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                              <span style={{ width: "8px", height: "8px", backgroundColor: priority.color, borderRadius: "50%", flexShrink: 0 }}></span>
                                              {priority.label}
                                            </span>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ color: "#98A2B3", fontSize: "13px", fontStyle: "italic" }}>No tasks found</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewSopInstance;
